import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { TrialStudent } from '~entities/student/api/get-all-trial-students';
import { useResolveTrialLifecycleConflict } from '~entities/student/api/resolve-trial-lifecycle-conflict';
import {
  getTrialLifecycleConflictMessage,
  hasTrialLifecycleConflict,
} from '~entities/student/trial-lifecycle.helpers';

import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/shadcn/ui/dialog';

interface TrialLifecycleConflictCellProps {
  trial: TrialStudent;
}

export function TrialLifecycleConflictCell({
  trial,
}: TrialLifecycleConflictCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const resolveMutation = useResolveTrialLifecycleConflict();
  const conflict = trial.lifecycleConflict;

  if (!hasTrialLifecycleConflict(conflict)) {
    return <div className='px-4 text-muted-foreground'>—</div>;
  }

  const message = getTrialLifecycleConflictMessage(conflict);

  const handleResolve = async () => {
    try {
      await resolveMutation.mutateAsync({ trialId: trial._id });
      toast.success('Trial marked as reviewed.');
      setIsOpen(false);
    } catch {
      toast.error('We could not update this trial. Please try again.');
    }
  };

  return (
    <div className='px-4'>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Badge
            variant='outline'
            title={message}
            className='cursor-pointer gap-1.5 border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100'
          >
            <AlertTriangle className='size-3' />
            Needs review
          </Badge>
        </DialogTrigger>
        <DialogContent className='sm:max-w-[480px]'>
          <DialogHeader>
            <DialogTitle>Trial needs review</DialogTitle>
            <DialogDescription>
              {trial.studentName
                ? `${trial.studentName}'s trial may not match the booking calendar.`
                : 'This trial may not match the booking calendar.'}
            </DialogDescription>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>{message}</p>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => void handleResolve()}
              disabled={resolveMutation.isPending}
            >
              {resolveMutation.isPending ? (
                <Loader2 className='animate-spin' />
              ) : (
                'Mark as reviewed'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
